import { useState, useEffect } from "react";
import { useAppSelector } from "../../../../store/types";
import ClearIcon from "@mui/icons-material/Clear";
import { Box, TextField, IconButton, Autocomplete } from "@mui/material";
import {
  MEDIA_FILE_DOES_NOT_EXISTS,
  MEDIA_FILE_NOT_VALID,
  AUDIO_FILE_REQUIRED_FOR_STATUS,
} from "../../../../constants";
import { getMediaMgtDataSelector } from "../../../../selectors/mediaMgtSelector";
import {
  MediaItem,
  MediaMgtDataContainer,
} from "../../../../slices/mediaMgtSlice";

interface MediaOption {
  name: string;
  value: string;
}

interface Props {
  data: MediaOption[];
  handleSelectChange?: ((value: string | null) => void) | null;
  value?: string | null;
  disabled?: boolean;
  required?: boolean;
  label?: React.ReactNode | null;
  onErrorChange?: ((hasError: boolean) => void) | null;
  defaultValue?: string;
  status?: string;
}

const MediafileAutoComplete = ({
  data,
  handleSelectChange = null,
  value,
  disabled,
  required,
  label = null,
  onErrorChange = null,
  status,
}: Props) => {
  const [selectedValue, setSelectedValue] = useState<MediaOption | null>(null);
  const [options, setOptions] = useState<MediaOption[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasError, setHasError] = useState(false);

  const mediaMgtData = useAppSelector(getMediaMgtDataSelector);

  const getMediaItems = (): MediaItem[] => {
    if (!mediaMgtData) return [];
    if (Array.isArray(mediaMgtData)) return mediaMgtData as MediaItem[];
    return (mediaMgtData as MediaMgtDataContainer).data ?? [];
  };

  useEffect(() => {
    setOptions(data);
    setSelectedValue(null);
  }, [data]);

  useEffect(() => {
    if (value) {
      const mediaItems = getMediaItems();
      const result = mediaItems.find((item) => item.filePath === value);

      if (result) {
        setSelectedValue({
          name: result.fileName as string,
          value,
        });
        setErrorMessage(null);
        setHasError(false);
      } else {
        setSelectedValue(null);
        setErrorMessage(MEDIA_FILE_NOT_VALID);
        setHasError(true);
      }
    } else {
      // Show error when audio file is empty for Active/Scheduled status or when field is required
      if (status === "Active" || status === "Scheduled" || required) {
        setErrorMessage(AUDIO_FILE_REQUIRED_FOR_STATUS);
        setHasError(true);
      } else {
        setErrorMessage(null);
        setHasError(false);
      }
    }
  }, [value, mediaMgtData, status, required]);

  useEffect(() => {
    if (onErrorChange) {
      onErrorChange(hasError);
    }
  }, [hasError, onErrorChange]);

  const handleHighlightChange = (
    _event: React.SyntheticEvent,
    option: MediaOption | null,
    reason: string
  ) => {
    if (option && reason === "keyboard") {
      setSelectedValue(option);
    }
  };

  const handleOnChange = (
    _event: React.SyntheticEvent,
    newValue: MediaOption | null
  ) => {
    const selected = newValue?.value ?? null;
    setSelectedValue(newValue);
    if (handleSelectChange) handleSelectChange(selected);
    setErrorMessage(null);
    setHasError(false);
  };

  const handleClearButtonClick = () => {
    setSelectedValue(null);
    if (handleSelectChange) handleSelectChange(null);
    // Show error when clearing audio file for Active/Scheduled status or when field is required
    if (status === "Active" || status === "Scheduled" || required) {
      setErrorMessage(AUDIO_FILE_REQUIRED_FOR_STATUS);
      setHasError(true);
    } else {
      setErrorMessage(null);
      setHasError(false);
    }
  };

  return (
    <Box>
      <Autocomplete
        className="formControl"
        size="small"
        disableClearable
        disablePortal
        options={!options ? [{ name: "loading...", value: "" }] : options}
        autoHighlight
        noOptionsText={
          <Box sx={{ fontSize: 14 }}>{MEDIA_FILE_DOES_NOT_EXISTS}</Box>
        }
        disabled={disabled}
        value={selectedValue}
        isOptionEqualToValue={(option, val) => option.name === val.name}
        onHighlightChange={handleHighlightChange}
        onChange={handleOnChange}
        ListboxProps={{
          sx: { fontSize: 13 },
        }}
        getOptionLabel={(option) => option.name ?? ""}
        renderOption={(props, option) => (
          <Box component={"li"} {...props}>
            {option.name}
          </Box>
        )}
        renderInput={(params) => (
          <Box sx={{ position: "relative" }}>
            <TextField
              {...params}
              size="small"
              label={label}
              required={required}
              error={hasError}
              helperText={errorMessage}
            />
            {selectedValue && (
              <IconButton
                className="clearButton"
                disableFocusRipple
                disableRipple
                onClick={handleClearButtonClick}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
        )}
      />
    </Box>
  );
};

export default MediafileAutoComplete;
